import React, { useEffect, useState } from "react";
import PageHeader from "../../components/ui/PageHeader";
import { HOME_CRUMB } from "../../components/ui/Breadcrumbs";
import Button from "../../components/ui/Button";
import DataTable, { DataTableColumn } from "../../components/ui/DataTable";
import RowActionsMenu, { RowMenuItem } from "../../components/ui/RowActionsMenu";
import EditDspOrganizationModal from "../../components/modal/EditDspOrganizationModal";
import AddDspOrganizationModal from "../../components/modal/AddDspOrganizationModal";
import InviteDspOrganizationModal from "../../components/modal/InviteDspOrganizationModal";
import ViewDspUsersModal from "../../components/modal/ViewDspUsersModal";
import ConfirmationModal from "../../components/modal/ConfirmationModal";
import Swal from "sweetalert2";
import { adminApi } from "../../repositories/admin";
import { PencilIcon, TrashIcon, UserGroupIcon } from "@heroicons/react/24/outline";

interface DspOrganization {
  id: string;
  name: string;
  labels: { id: number; name: string }[];
  numberOfUsers: number;
  createdAt: string;
  updatedAt: string;
}

const MAX_VISIBLE_LABELS = 3;

/**
 * The label list for one organization. Long lists dominated the row, so only
 * the first few show until the user asks for the rest — each row keeps its own
 * expanded state, so opening one doesn't expand the table.
 */
const LabelTags: React.FC<{ names: string[] }> = ({ names }) => {
  const [expanded, setExpanded] = useState(false);

  if (names.length === 0) return <span className="text-neutral-400">N/A</span>;

  const hidden = names.length - MAX_VISIBLE_LABELS;
  const visible = expanded ? names : names.slice(0, MAX_VISIBLE_LABELS);

  return (
    <div className="flex flex-wrap items-center gap-1 max-w-xs">
      {visible.map((name) => (
        <span
          key={name}
          className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium border rounded-full text-neutral-700 bg-neutral-100 border-neutral-200"
        >
          {name}
        </span>
      ))}
      {hidden > 0 && (
        <Button
          type="button"
          variant="link"
          size="inline"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          className="text-xs"
        >
          {expanded ? "Show less" : `+${hidden} see more`}
        </Button>
      )}
    </div>
  );
};

const DspOrganizations = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isViewUsersModalOpen, setIsViewUsersModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [dspOrgs, setDspOrgs] = useState<DspOrganization[]>([]);
  // Without this the table showed its empty state for the first paint, before
  // the request resolved.
  const [loading, setLoading] = useState(true);
  const [currentDsp, setCurrentDsp] = useState<DspOrganization | null>(null);

  const fetchDspOrganizations = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getDspOrganizations();
      setDspOrgs(response.data.data);
    } catch (error: any) {
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to fetch DSP organizations",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (id: string) => {
    const dspToEdit = dspOrgs.find((dsp) => dsp.id === id);
    if (dspToEdit) {
      setCurrentDsp(dspToEdit);
      setIsEditModalOpen(true);
    }
  };

  const handleAdd = async (
    name: string,
    labelIds: number[]
  ) => {
    try {
      const { data } = await adminApi.createDspOrganization({
        name,
        labelIds,
      });
      if (data?.success) {
        Swal.fire("Success", "DSP organization added successfully", "success");
        fetchDspOrganizations();
      }
    } catch (error: any) {
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to add DSP organization",
        "error"
      );
    }
  };

  const handleSaveEdit = async (
    name: string,
    labelIds: number[]
  ) => {
    if (!currentDsp) {
      return Swal.fire("Error", "No DSP organization selected for editing", "error");
    }
    try {
      const { data } = await adminApi.updateDspOrganization(currentDsp.id, {
        name,
        labelIds,
      });
      if (data?.success) {
        Swal.fire("Success", "DSP organization updated successfully", "success");
        fetchDspOrganizations();
      }
      setIsEditModalOpen(false);
    } catch (error: any) {
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to update DSP organization",
        "error"
      );
    }
  };

  const handleViewUsers = (id: string) => {
    const dsp = dspOrgs.find((dsp) => dsp.id === id);
    if (dsp) {
      setCurrentDsp(dsp);
      setIsViewUsersModalOpen(true);
    }
  };

  const handleDeleteClick = (id: string) => {
    const dsp = dspOrgs.find((dsp) => dsp.id === id);
    if (dsp) {
      setCurrentDsp(dsp);
      setIsDeleteModalOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!currentDsp) return;

    try {
      const { data } = await adminApi.deleteDspOrganization(currentDsp.id);
      if (data?.success) {
        Swal.fire("Success", "DSP organization deleted successfully", "success");
        fetchDspOrganizations();
      }
    } catch (error: any) {
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to delete DSP organization",
        "error"
      );
    }
  };

  useEffect(() => {
    fetchDspOrganizations();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const columns: DataTableColumn<DspOrganization>[] = [
    { key: "id", header: "Id", cell: (dsp) => <span className="text-neutral-600">{dsp.id}</span> },
    {
      key: "name",
      header: "DSP Name",
      cell: (dsp) => <span className="font-medium whitespace-nowrap">{dsp.name}</span>,
    },
    {
      key: "labels",
      header: "Labels",
      // Rendered as neutral category tags rather than one long comma string:
      // the full list dominated the row, and this is purely presentational —
      // the underlying data is untouched.
      cell: (dsp) => <LabelTags names={dsp.labels.map((l) => l.name).filter(Boolean)} />,
    },
    {
      key: "numberOfUsers",
      header: "No of Users",
      align: "center",
      cell: (dsp) => <span className="tabular-nums text-neutral-600">{dsp.numberOfUsers}</span>,
    },
    {
      key: "createdAt",
      header: "Created At",
      cell: (dsp) => (
        <span className="text-neutral-600 whitespace-nowrap">{formatDate(dsp.createdAt)}</span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "center",
      cell: (dsp) => (
        <RowActionsMenu label={`Actions for ${dsp.name || "organization"}`}>
          <RowMenuItem icon={<PencilIcon className="w-4 h-4" />} onClick={() => handleEdit(dsp.id)}>
            Edit
          </RowMenuItem>
          <RowMenuItem
            icon={<UserGroupIcon className="w-4 h-4" />}
            onClick={() => handleViewUsers(dsp.id)}
          >
            View Users
          </RowMenuItem>
          <RowMenuItem
            tone="danger"
            icon={<TrashIcon className="w-4 h-4" />}
            onClick={() => handleDeleteClick(dsp.id)}
          >
            Delete
          </RowMenuItem>
        </RowActionsMenu>
      ),
    },
  ];

  return (
    <div className="py-2 sm:p-6">
      {/*
        One primary action: this page manages organizations, and inviting a user
        only makes sense once one exists — so "Add Organization" leads and
        "Invite DSP User" is the supporting outline action.
      */}
      <PageHeader
        breadcrumbs={[HOME_CRUMB, { label: "DSP Organizations" }]}
        className="mb-6"
        title="DSP Organizations"
        description="DSP partners, the labels they can see, and their users."
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => setIsInviteModalOpen(true)}
              className="w-full sm:w-auto"
            >
              Invite DSP User
            </Button>
            <Button onClick={() => setIsAddModalOpen(true)} className="w-full sm:w-auto">
              Add Organization
            </Button>
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={dspOrgs}
        getRowKey={(dsp) => dsp.id}
        loading={loading}
        emptyMessage={
          <span className="block">
            <span className="block text-sm font-medium text-black">No DSP organizations found</span>
            <span className="block mt-1 text-sm text-neutral-500">
              Add an organization to get started.
            </span>
          </span>
        }
      />


      <AddDspOrganizationModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAdd}
      />

      {/* Edit DSP Modal */}
      {currentDsp && (
        <EditDspOrganizationModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          dspId={currentDsp.id}
          initialDspName={currentDsp.name}
          initialLabels={currentDsp.labels.map(l => ({ labelId: l.id, labelName: l.name }))}
          initialNoOfUser={currentDsp.numberOfUsers}
          onSave={handleSaveEdit}
        />
      )}

      {/* Invite User Modal */}
      <InviteDspOrganizationModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
      />

      {/* View Users Modal */}
      {currentDsp && (
        <ViewDspUsersModal
          isOpen={isViewUsersModalOpen}
          onClose={() => setIsViewUsersModalOpen(false)}
          organizationId={currentDsp.id}
          organizationName={currentDsp.name}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete DSP Organization"
        message={`Are you sure you want to delete "${currentDsp?.name}"? This action cannot be undone.`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        isDestructive={true}
      />
    </div>
  );
};

export default DspOrganizations;
